import type { DataProvider } from '@refinedev/core';
import Config from 'src/common/Config';
import { ApiError } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import * as Sentry from '@sentry/react';
import { redirectToLogin } from 'src/auth/RedirectToLoginPage';

const API_URL = Config.getApiUrl();

export const dataProvider: DataProvider = {
  getOne: async ({ resource, id }) => {
    const idPath = id ? `/${id}` : '';
    const url = `${API_URL}/${resource}${idPath}`;
    return await fetchWrapper(
      url,
      {
        credentials: 'include',
      },
      'getOne',
    );
  },
  update: async ({ resource, id, variables }) => {
    const idPath = id ? `/${id}` : '';
    const url = `${API_URL}/${resource}${idPath}`;

    return await fetchWrapper(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(variables),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'update',
    );
  },
  getList: async ({ resource, filters, pagination }) => {
    let url = `${API_URL}/${resource}`;
    const queryParams = new URLSearchParams();

    if (filters) {
      filters.forEach(filter => {
        if ('field' in filter) {
          if (filter.operator === 'eq') {
            queryParams.append(filter.field, filter.value);
          } else {
            queryParams.append(`${filter.field}_${filter.operator}`, filter.value);
          }
        }
      });
    }

    if (pagination && pagination.mode === 'server') {
      if (pagination.current) {
        queryParams.append('page', pagination.current.toString());
      }
      if (pagination.pageSize) {
        queryParams.append('size', pagination.pageSize.toString());
      }
    }

    url = `${url}?${queryParams.toString()}`;
    const result = await fetchWrapper(
      url,
      {
        credentials: 'include',
      },
      'getList',
    );

    if (result.data && Array.isArray(result.data.items) && typeof result.data.total === 'number') {
      return {
        data: result.data.items,
        total: result.data.total,
      };
    }

    return {
      ...result,
      total: result?.data?.total ?? 0,
    };
  },
  create: async ({ resource, variables, meta }) => {
    const query = meta?.query ? `?${new URLSearchParams(meta.query).toString()}` : '';
    return await fetchWrapper(
      `${API_URL}/${resource}${query}`,
      {
        method: 'POST',
        body: JSON.stringify(variables ?? {}),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'create',
    );
  },
  deleteOne: async ({ resource, id }) => {
    return await fetchWrapper(
      `${API_URL}/${resource}/${id}`,
      {
        method: 'DELETE',
        credentials: 'include',
      },
      'deleteOne',
    );
  },
  deleteMany: async ({ resource, ids }) => {
    return await fetchWrapper(
      `${API_URL}/${resource}/delete-request`,
      {
        method: 'POST',
        body: JSON.stringify(ids),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'deleteMany',
    );
  },
  custom: async ({ url, method, headers, payload }) => {
    return await fetchWrapper(
      `${API_URL}/${url}`,
      {
        method,
        body: payload ? JSON.stringify(payload) : undefined,
        headers: {
          'Content-Type': 'application/json',
          ...(headers || {}),
        },
        credentials: 'include',
      },
      `custom ${method.toUpperCase()}`,
    );
  },

  getApiUrl: () => API_URL,
  // Optional methods:
  // getMany: () => { /* ... */ },
  // createMany: () => { /* ... */ },
  // deleteMany: () => { /* ... */ },
  // updateMany: () => { /* ... */ },
};

async function fetchWrapper(
  url: string,
  init: RequestInit = {},
  operation = 'undefined',
  { timeoutMs = 25000 }: { timeoutMs?: number } = {},
) {
  const started = performance.now();
  const reqId = Math.random().toString(16).slice(2) + '-' + Date.now().toString(36);
  const hdrs = new Headers(init.headers || {});
  hdrs.set('x-af-cid', reqId);
  const opts: RequestInit = {
    ...init,
    headers: hdrs,
    mode: init.mode ?? 'cors',
    credentials: init.credentials ?? 'include',
  };

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(`timeout ${timeoutMs}ms`), timeoutMs);
  opts.signal = controller.signal;

  try {
    const res = await fetch(url, opts);
    clearTimeout(t);
    return await handleResponse(res, url.toString(), operation);
  } catch (err) {
    if (err instanceof UnauthenticatedError) {
      throw err;
    }

    clearTimeout(t);

    const duration = performance.now() - started;
    Sentry.withScope(scope => {
      scope.setTag('reqId', reqId);
      scope.setTag('operation', operation);
      scope.setContext('fetch', {
        url: url.toString(),
        method: opts.method ?? 'No method in opts',
        mode: opts.mode,
        credentials: opts.credentials,
        duration_ms: Math.round(duration),
        errorName: (err as { name?: string })?.name ?? 'No error name',
        errorMessage: (err as { message?: string })?.message ?? 'No error message',
      });
      Sentry.captureException(err);
    });

    logAndThrow(err, url.toString(), operation);
  }
}

class UnauthenticatedError extends Error {
  constructor() {
    super('401 custom error to trigger login redirect');
  }
}

async function handleResponse(response: Response, resource: string, operation: string) {
  if (response.status === 401) {
    if (!response.url?.includes('localhost') && !response.url?.includes('afternoon')) {
      // Adding this more for an informative log in case we have a misconfiguration
      // The check is not very robust. However, it should do the job for now.
      // Keeping the same behaviour in this scenario but logging an error, so we get alerted if it happens
      Logger.error(
        `Received 401 from unexpected URL: ${response.url}`,
        new Error('401 from unexpected URL'),
      );
    }
    await redirectToLogin();
    throw new UnauthenticatedError();
  }
  if (response.status === 204) {
    return { data: null };
  }

  if (response.status < 200 || response.status > 299) {
    Logger.error(
      'Unsuccessful response',
      new Error(`HTTP ${response.status} error for ${operation} on ${resource}`),
      {
        resource,
        operation,
        status: response.status,
        body: await getResponseBodyAsText(response.clone()),
      },
    );
    throw await ApiError.fromApiResponse(response);
  }

  try {
    const data = await response.json();
    return { data };
  } catch (error) {
    Logger.error('Failed to retrieve response JSON', error, {
      resource,
      operation,
      body: await getResponseBodyAsText(response),
    });
    throw new Error('Failed to retrieve response data');
  }
}

async function getResponseBodyAsText(response: Response) {
  try {
    return await response.text();
  } catch (e) {
    Logger.error('Could not read response body as text', e, {
      status: response.status,
      url: response.url,
      unparsedBody: response.body,
    });
    return 'Could not read response body as text';
  }
}

function logAndThrow(error: unknown, resource: string, operation: string): never {
  Logger.error(`Fetch error for ${operation} ${resource}`, error, {
    resource,
    operation,
  });
  throw error;
}
